/**
 * Whole-tower reactions that the baked clips don't have (art_review/towers.md F8, vfx.md F11), as pure curves so
 * they can be tested without WebGL. Times are seconds since the event.
 */
export const TowerFeel = {
  /** A shot's squash: body height 1 → 0.96 → 1.02 → 1 over 0.25 s (compress on the pull, stretch on the release). */
  SquashSeconds: 0.25,
  squash(t: number): number {
    if (!(t >= 0) || t >= TowerFeel.SquashSeconds) return 1;
    const keys = TowerFeel.squashKeys;
    for (let i = 1; i < keys.length; i++) {
      const [t1, v1] = keys[i];
      if (t <= t1) {
        const [t0, v0] = keys[i - 1];
        const u = (t - t0) / (t1 - t0), s = u * u * (3 - 2 * u);
        return v0 + (v1 - v0) * s;
      }
    }
    return 1;
  },
  squashKeys: [[0, 1], [0.06, 0.96], [0.14, 1.02], [0.25, 1]] as const,

  /** Heavy towers (cannon, mortar) kick back along the shot: 3 cm, out in 40 ms, home by 0.24 s. */
  KickMetres: 0.03,
  KickSeconds: 0.24,
  kick(t: number): number {
    if (!(t >= 0) || t >= TowerFeel.KickSeconds) return 0;
    return t < 0.04 ? t / 0.04 : 1 - (t - 0.04) / (TowerFeel.KickSeconds - 0.04);
  },

  /** Placement drop-in: the tower falls the last half metre in 120 ms, accelerating, then lands with a squash. */
  DropSeconds: 0.12,
  DropHeight: 0.5,
  drop(t: number): number {
    if (!(t >= 0) || t >= TowerFeel.DropSeconds) return 0;
    const p = t / TowerFeel.DropSeconds;
    return TowerFeel.DropHeight * (1 - p * p);
  },

  /**
   * Tier-up ascension (ART_WOW_PASS Phase 2 §2): the old tower gathers (height 1 → 0.88, width 1 → 1.05) over
   * `AscendSquash`, the model swaps under a flash, and the new one rises height 0.55 → ~1.08 → 1 (out-back) over
   * `AscendRise`, width the volume-preserving inverse.
   */
  AscendSquash: 0.3,
  AscendRise: 0.45,
  ascendGather(t: number): { y: number; xz: number } {
    const k = Math.min(1, Math.max(0, t / TowerFeel.AscendSquash)), e = k * k;
    return { y: 1 - 0.12 * e, xz: 1 + 0.05 * e };
  },
  ascendRise(t: number): { y: number; xz: number } {
    const k = Math.min(1, Math.max(0, t / TowerFeel.AscendRise)), c1 = 1.9;
    const back = 1 + (c1 + 1) * Math.pow(k - 1, 3) + c1 * Math.pow(k - 1, 2);
    const y = 0.55 + 0.45 * back;
    return { y, xz: 1 / Math.sqrt(Math.max(0.3, y)) };
  },

  /**
   * A `fire` clip plays faster when it is longer than the fire interval, so it ends by the next shot (the archer's
   * 0.7 s clip against its 0.5 s interval, everything at ×3): cut short, the next shot restarted it from the draw —
   * the re-nocked arrow popped back — or, under the restart threshold, the shot left a bow that never drew. `FireFit`
   * of the interval is the clip's budget; `MaxFireRate` keeps a very fast tower's clip from blurring into a twitch.
   */
  FireFit: 0.95,
  MaxFireRate: 5,
  fireRate(clipSeconds: number, intervalSeconds: number): number {
    if (!(clipSeconds > 0) || !(intervalSeconds > 0)) return 1;
    return Math.min(TowerFeel.MaxFireRate, Math.max(1, clipSeconds / (intervalSeconds * TowerFeel.FireFit)));
  },

  /**
   * The round the `fire` clip shows loaded (frames at RELEASE_FPS, the clips' 30): the archer's nocked `arrow`, the ballista's `bolt`. The
   * view owns its visibility from the draw to the reload, because the clips disagree with the real round: the arrow
   * flew 0.3 m ahead and shrank away as the projectile appeared behind it (two arrows), and the ballista's bolt shrank
   * to nothing during the draw so its projectile came from nowhere. Shown until the release frame, hidden from there
   * to `reload` (where the clip grows the next one in), the clip's own after.
   */
  Nocked: { archer: { part: "arrow", reload: 15 }, ballista: { part: "bolt", reload: 16.25 } } as Readonly<Record<string, { readonly part: string; readonly reload: number }>>,
  /** The nocked round's scale `frame` frames into the `fire` clip, released on `release`; null: leave the clip's. */
  nockedScale(frame: number, release: number, reload: number): number | null {
    if (!(frame >= 0) || frame >= reload) return null;
    return frame < release ? 1 : 0;
  },

  /** Glow flash on each shot (the `look` agent's per-object glow API): ×2.5 for 0.12 s. */
  GlowGain: 2.5,
  GlowSeconds: 0.12,
  /** Idle life (towers.md F9): an arc between two coils every 2–4 s; an ember off a fire tower every 0.4 s. */
  ArcEvery: [2, 4] as const,
  EmberEvery: 0.4,
};
